<template>
    <div class="py-12">
        <AiChatTabs />

        <div class="mb-6 flex flex-wrap items-start justify-between gap-4">
            <div>
                <h1 class="text-2xl font-semibold text-gray-800">
                    {{ t("vue.ai_dashboard") }}
                </h1>
                <p class="mt-1 text-sm text-gray-500">
                    {{ t("vue.ai_dashboard_subtitle") }}
                </p>
            </div>

            <div class="flex flex-wrap gap-2">
                <inertia-link :href="route('ai-chat.index')">
                    <AtomicButton size="sm" color="white">
                        {{ t("vue.ai_dashboard_open_chat") }}
                    </AtomicButton>
                </inertia-link>
                <inertia-link :href="route('ai-content.index')">
                    <AtomicButton size="sm" color="white">
                        {{ t("vue.ai_dashboard_view_content") }}
                    </AtomicButton>
                </inertia-link>
                <AtomicButton
                    size="sm"
                    color="yellow"
                    :disabled="loading"
                    @click="regenerate"
                >
                    {{ t("vue.ai_dashboard_regenerate") }}
                </AtomicButton>
            </div>
        </div>

        <!-- Dynamic briefing -->
        <section class="relative">
            <AiRobotBackdrop />

            <div
                class="relative z-10 rounded-2xl border border-gray-200 bg-white/55 shadow-card backdrop-blur-md"
            >
                <div
                    class="flex items-center justify-between gap-3 rounded-t-2xl border-b border-gray-200 bg-white/50 px-6 py-4"
                >
                    <AiRobotHeader
                        :title="t('vue.ai_dashboard_briefing_title')"
                        :subtitle="t('vue.ai_dashboard_briefing_header_hint')"
                        :wave="t('vue.ai_chat_welcome_wave')"
                    />
                    <AtomicButton
                        v-if="state.briefing && !loading"
                        size="sm"
                        color="yellow"
                        :disabled="asking"
                        @click="discussBrief"
                    >
                        {{ t("vue.ai_dashboard_discuss_brief") }}
                    </AtomicButton>
                </div>

                <div class="p-6">
                    <!-- Loading -->
                    <div
                        v-if="loading"
                        class="flex flex-col items-center justify-center gap-3 py-12 text-center"
                    >
                        <div class="relative">
                            <div class="generating-bubble">
                                {{ t("vue.ai_dashboard_generating_bubble") }}
                            </div>
                            <div class="briefing-robot">
                                <AnimatedRobot />
                            </div>
                        </div>
                        <p class="text-sm font-medium text-gray-600">
                            {{ t("vue.ai_dashboard_generating") }}
                        </p>
                    </div>

                    <!-- Empty -->
                    <div
                        v-else-if="!state.briefing"
                        class="flex flex-col items-center justify-center gap-4 py-12 text-center"
                    >
                        <div class="briefing-robot">
                            <AnimatedRobot />
                        </div>
                        <p class="text-sm text-gray-500">
                            {{ t("vue.ai_dashboard_empty") }}
                        </p>
                        <AtomicButton
                            size="sm"
                            color="yellow"
                            @click="regenerate"
                        >
                            {{ t("vue.ai_dashboard_regenerate") }}
                        </AtomicButton>
                    </div>

                    <!-- Briefing -->
                    <div v-else>
                        <AiChatMarkdown
                            class="text-gray-800"
                            :content="state.briefing.summary"
                        />

                        <div
                            v-if="state.briefing.highlights.length"
                            class="mt-5 grid gap-2 sm:grid-cols-2"
                        >
                            <button
                                v-for="(highlight, index) in state.briefing
                                    .highlights"
                                :key="`h-${index}`"
                                type="button"
                                class="group flex w-full cursor-pointer items-start gap-2 rounded-lg border p-3 text-left transition hover:brightness-95"
                                :class="severityClass(highlight.severity)"
                                :disabled="asking"
                                @click="askHighlight(highlight)"
                            >
                                <component
                                    :is="severityIcon(highlight.severity)"
                                    class="mt-0.5 h-4 w-4 shrink-0"
                                />
                                <div class="min-w-0 flex-1">
                                    <p
                                        class="text-sm font-semibold leading-tight"
                                    >
                                        {{ highlight.title }}
                                    </p>
                                    <p class="mt-0.5 text-xs opacity-80">
                                        {{ highlight.description }}
                                    </p>
                                </div>
                                <ChatBubbleLeftRightIcon
                                    class="h-3.5 w-3.5 shrink-0 opacity-0 transition group-hover:opacity-70"
                                />
                            </button>
                        </div>

                        <div v-if="sortedRecommendations.length" class="mt-5">
                            <p
                                class="mb-2 text-xs font-medium uppercase tracking-wide text-gray-400"
                            >
                                {{ t("vue.ai_dashboard_recommendations") }}
                            </p>
                            <ul class="space-y-2">
                                <li
                                    v-for="(
                                        rec, index
                                    ) in sortedRecommendations"
                                    :key="`r-${index}`"
                                >
                                    <button
                                        type="button"
                                        class="group flex w-full cursor-pointer items-start gap-2.5 rounded-lg border border-gray-200 bg-white/40 p-3 text-left transition hover:border-[#FF421E]/40 hover:bg-[#FF421E]/5"
                                        :disabled="asking"
                                        @click="solveRec(rec)"
                                    >
                                        <component
                                            :is="priorityIcon(rec.priority)"
                                            class="mt-0.5 h-4 w-4 shrink-0"
                                            :class="
                                                priorityIconClass(rec.priority)
                                            "
                                        />
                                        <div class="min-w-0 flex-1">
                                            <div
                                                class="flex flex-wrap items-center gap-x-2 gap-y-0.5"
                                            >
                                                <span
                                                    class="text-sm font-semibold text-gray-800"
                                                >
                                                    {{ rec.title }}
                                                </span>
                                                <span
                                                    class="rounded px-1.5 py-0.5 text-[10px] font-medium uppercase"
                                                    :class="
                                                        priorityClass(
                                                            rec.priority,
                                                        )
                                                    "
                                                >
                                                    {{ rec.priority }}
                                                </span>
                                            </div>
                                            <p
                                                v-if="rec.suggested_action"
                                                class="mt-0.5 text-xs font-medium text-gray-700"
                                            >
                                                {{ rec.suggested_action }}
                                            </p>
                                            <p
                                                v-else-if="rec.reason"
                                                class="mt-0.5 text-xs text-gray-500"
                                            >
                                                {{ rec.reason }}
                                            </p>
                                        </div>
                                        <span
                                            class="shrink-0 self-center whitespace-nowrap text-xs font-medium text-[#FF421E] opacity-0 transition group-hover:opacity-100"
                                        >
                                            {{ t("vue.ai_dashboard_solve") }} →
                                        </span>
                                    </button>
                                </li>
                            </ul>
                        </div>

                        <div
                            v-if="state.briefing.suggested_questions.length"
                            class="mt-6"
                        >
                            <p
                                class="mb-2 text-xs font-medium uppercase tracking-wide text-gray-400"
                            >
                                {{ t("vue.ai_dashboard_suggested") }}
                            </p>
                            <div class="grid gap-2 sm:grid-cols-2">
                                <button
                                    v-for="(question, index) in state.briefing
                                        .suggested_questions"
                                    :key="`q-${index}`"
                                    type="button"
                                    class="cursor-pointer rounded-xl border border-gray-300 bg-white/40 px-4 py-2.5 text-left text-sm text-gray-700 transition hover:border-[#FF421E]/40 hover:bg-[#FF421E]/5"
                                    :disabled="asking"
                                    @click="ask(question)"
                                >
                                    {{ question }}
                                </button>
                            </div>
                        </div>

                        <p
                            v-if="state.briefing.generated_at"
                            class="mt-6 text-xs text-gray-400"
                        >
                            {{ t("vue.ai_dashboard_generated_at") }}
                            {{ formatDate(state.briefing.generated_at) }}
                        </p>
                    </div>
                </div>
            </div>
        </section>

        <!-- Deterministic activity -->
        <div class="mt-6 grid gap-4 lg:grid-cols-3">
            <section class="rounded-xl border border-gray-200 bg-white p-4">
                <p class="mb-2 text-sm font-semibold text-gray-800">
                    {{ t("vue.ai_dashboard_recent_chats") }}
                </p>
                <p
                    v-if="!dashboard.recent_chats.length"
                    class="text-xs text-gray-400"
                >
                    {{ t("vue.ai_dashboard_no_chats") }}
                </p>
                <ul v-else class="max-h-56 space-y-0.5 overflow-y-auto">
                    <li
                        v-for="chat in dashboard.recent_chats"
                        :key="`chat-${chat.id}`"
                    >
                        <inertia-link
                            :href="route('ai-chat.show', { chat: chat.id })"
                            class="block rounded-md px-2 py-1.5 transition hover:bg-[#FF421E]/5"
                        >
                            <span class="line-clamp-1 text-sm text-gray-700">
                                {{ chat.title }}
                            </span>
                            <span
                                v-if="chat.last_message_at"
                                class="text-[11px] text-gray-400"
                            >
                                {{ formatDate(chat.last_message_at) }}
                            </span>
                        </inertia-link>
                    </li>
                </ul>
            </section>

            <section class="rounded-xl border border-gray-200 bg-white p-4">
                <p class="mb-2 text-sm font-semibold text-gray-800">
                    {{ t("vue.ai_dashboard_recent_contents") }}
                </p>
                <p
                    v-if="!dashboard.recent_contents.length"
                    class="text-xs text-gray-400"
                >
                    {{ t("vue.ai_dashboard_no_contents") }}
                </p>
                <ul v-else class="max-h-56 space-y-0.5 overflow-y-auto">
                    <li
                        v-for="content in dashboard.recent_contents"
                        :key="`content-${content.id}`"
                    >
                        <inertia-link
                            :href="
                                route('ai-content.index', {
                                    content: content.id,
                                })
                            "
                            class="block rounded-md px-2 py-1.5 transition hover:bg-[#FF421E]/5"
                        >
                            <span class="line-clamp-1 text-sm text-gray-700">
                                {{ content.title }}
                            </span>
                            <span
                                v-if="content.created_at"
                                class="text-[11px] text-gray-400"
                            >
                                {{ formatDate(content.created_at) }}
                            </span>
                        </inertia-link>
                    </li>
                </ul>
            </section>

            <section class="rounded-xl border border-gray-200 bg-white p-4">
                <p class="mb-2 text-sm font-semibold text-gray-800">
                    {{ t("vue.ai_dashboard_recent_events") }}
                </p>
                <p
                    v-if="!dashboard.recent_events.length"
                    class="text-xs text-gray-400"
                >
                    {{ t("vue.ai_dashboard_no_events") }}
                </p>
                <ul v-else class="max-h-56 space-y-0.5 overflow-y-auto">
                    <li
                        v-for="(event, index) in dashboard.recent_events"
                        :key="`event-${index}`"
                        class="px-2 py-1.5"
                    >
                        <div class="flex items-center gap-1.5">
                            <span
                                class="h-1.5 w-1.5 shrink-0 rounded-full"
                                :class="eventDotClass(event.type)"
                            />
                            <span
                                class="text-[11px] font-medium uppercase tracking-wide text-gray-400"
                            >
                                {{ eventTypeLabel(event.type) }}
                            </span>
                            <span
                                v-if="event.created_at"
                                class="ml-auto shrink-0 text-[11px] text-gray-400"
                            >
                                {{ formatDate(event.created_at) }}
                            </span>
                        </div>
                        <p
                            v-if="event.message"
                            class="line-clamp-1 text-sm text-gray-700"
                        >
                            {{ event.message }}
                        </p>
                    </li>
                </ul>
            </section>
        </div>

        <div class="mt-4 grid gap-4 lg:grid-cols-2">
            <section class="rounded-xl border border-gray-200 bg-white p-4">
                <p class="mb-2 text-sm font-semibold text-gray-800">
                    {{ t("vue.ai_dashboard_recent_tags") }}
                </p>
                <p
                    v-if="!dashboard.recent_tags.length"
                    class="text-xs text-gray-400"
                >
                    {{ t("vue.ai_dashboard_no_tags") }}
                </p>
                <div
                    v-else
                    class="flex max-h-56 flex-wrap gap-1.5 overflow-y-auto"
                >
                    <inertia-link
                        v-for="tag in dashboard.recent_tags"
                        :key="`tag-${tag.id}`"
                        :href="route('ai-tags.index', { tags: tag.id })"
                        class="rounded-full border border-gray-200 bg-gray-50 px-2.5 py-0.5 text-xs text-gray-600 transition hover:border-[#FF421E]/40 hover:bg-[#FF421E]/5"
                    >
                        {{ tag.name }}
                        <span class="text-gray-400"
                            >({{ tag.usage_count }})</span
                        >
                    </inertia-link>
                </div>
            </section>

            <section
                v-if="teamSkillsActive"
                class="rounded-xl border border-gray-200 bg-white p-4"
            >
                <div class="mb-2 flex items-center justify-between">
                    <p class="text-sm font-semibold text-gray-800">
                        {{ t("vue.ai_dashboard_recent_skills") }}
                    </p>
                    <inertia-link
                        :href="route('ai-skills.index')"
                        class="text-xs font-medium text-[#FF421E] transition hover:underline"
                    >
                        {{ t("vue.ai_dashboard_all_skills") }} →
                    </inertia-link>
                </div>

                <p
                    v-if="!dashboard.recent_team_skills.length"
                    class="text-xs text-gray-400"
                >
                    {{ t("vue.ai_dashboard_no_skills") }}
                </p>

                <ul v-else class="max-h-56 space-y-1 overflow-y-auto">
                    <li
                        v-for="skill in dashboard.recent_team_skills"
                        :key="`skill-${skill.id}`"
                    >
                        <inertia-link
                            :href="route('ai-skills.index')"
                            class="block rounded-md border border-gray-200 px-2.5 py-1.5 transition hover:border-[#FF421E]/40 hover:bg-[#FF421E]/5"
                        >
                            <p
                                class="line-clamp-1 text-sm font-medium text-gray-800"
                            >
                                {{ skill.name }}
                            </p>
                            <p
                                v-if="skill.summary"
                                class="line-clamp-1 text-[11px] text-gray-400"
                            >
                                {{ skill.summary }}
                            </p>
                        </inertia-link>
                    </li>
                </ul>
            </section>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { computed, onUnmounted, reactive, ref } from "vue";
import { router, usePage } from "@inertiajs/vue3";
import route from "ziggy-js";
import type { Component } from "vue";
import {
    ExclamationTriangleIcon,
    ExclamationCircleIcon,
    InformationCircleIcon,
} from "@heroicons/vue/24/solid";
import { ChatBubbleLeftRightIcon } from "@heroicons/vue/24/outline";
import { useTranslations } from "@/composables/useTranslations";
import type {
    IAiDashboardData,
    IBriefing,
    IBriefingHighlight,
    IBriefingRecommendation,
    IBriefingState,
} from "@/types/AiChat/IAiDashboard";
import type { IRhizomPageProps } from "@/types/RhizomPageProps/RhizomPageProps";

const { t } = useTranslations();
const teamSkillsActive = computed(
    () => usePage<IRhizomPageProps>().props.inAppAiChatTeamSkillsActive,
);

const props = defineProps<{
    dashboard: IAiDashboardData;
    briefing: IBriefingState;
}>();

const state = reactive<{
    status: string;
    briefing: IBriefing | null;
}>({
    status: props.briefing.status,
    briefing: props.briefing.briefing,
});

const asking = ref(false);
const regenerating = ref(false);

const isGenerating = computed(() => state.status === "generating");
// The robot loader shows both while the server reports a generation and, on a
// synchronous queue, for the whole duration of a manual regenerate request.
const loading = computed(() => regenerating.value || isGenerating.value);

let pollTimer: number | undefined;
let pollFailures = 0;
const MAX_POLL_FAILURES = 5;

const stopPolling = () => {
    if (pollTimer !== undefined) {
        window.clearInterval(pollTimer);
        pollTimer = undefined;
    }
    pollFailures = 0;
};

const applyState = (next: IBriefingState) => {
    state.status = next.status;
    state.briefing = next.briefing;

    if (next.status !== "generating") {
        stopPolling();
    }
};

const poll = async () => {
    try {
        const { data } = await axios.get<IBriefingState>(
            route("ai-chat.dashboard.briefing.latest"),
        );
        pollFailures = 0;
        applyState(data);
    } catch {
        // A transient failure must not wedge the UI in "generating" — keep
        // retrying and only give up after several consecutive failures.
        pollFailures += 1;

        if (pollFailures >= MAX_POLL_FAILURES) {
            stopPolling();
            state.status = state.briefing ? "ready" : "empty";
        }
    }
};

const startPolling = () => {
    if (pollTimer !== undefined) {
        return;
    }
    pollTimer = window.setInterval(poll, 2500);
};

const regenerate = async () => {
    if (loading.value) {
        return;
    }

    regenerating.value = true;

    try {
        const { data } = await axios.post<IBriefingState>(
            route("ai-chat.dashboard.briefing.regenerate"),
        );
        applyState(data);
    } catch {
        state.status = state.briefing ? "ready" : "empty";
    } finally {
        regenerating.value = false;
    }

    if (state.status === "generating") {
        startPolling();
    }
};

const startChat = (message: string) => {
    asking.value = true;
    router.post(
        route("ai-chat.store"),
        { message },
        {
            onFinish: () => {
                asking.value = false;
            },
        },
    );
};

const ask = (question: string) => startChat(question);

const formatBrief = (briefing: IBriefing): string => {
    const parts: string[] = [briefing.summary];

    if (briefing.highlights.length) {
        parts.push(
            `${t("vue.ai_dashboard_highlights")}:\n${briefing.highlights
                .map((h) => `- ${h.title}: ${h.description}`)
                .join("\n")}`,
        );
    }

    if (briefing.recommendations.length) {
        parts.push(
            `${t("vue.ai_dashboard_recommendations")}:\n${briefing.recommendations
                .map(
                    (r) =>
                        `- [${r.priority}] ${r.title}: ${
                            r.suggested_action || r.reason
                        }`,
                )
                .join("\n")}`,
        );
    }

    return parts.join("\n\n");
};

const discussBrief = () => {
    if (!state.briefing) {
        return;
    }
    startChat(
        t("vue.ai_dashboard_ask_brief", { brief: formatBrief(state.briefing) }),
    );
};

const solveRec = (rec: IBriefingRecommendation) => {
    startChat(
        t("vue.ai_dashboard_ask_rec", {
            title: rec.title,
            action: rec.suggested_action || rec.reason,
        }),
    );
};

const askHighlight = (highlight: IBriefingHighlight) => {
    startChat(
        t("vue.ai_dashboard_ask_highlight", {
            title: highlight.title,
            description: highlight.description,
        }),
    );
};

const PRIORITY_RANK: Record<string, number> = { high: 0, medium: 1, low: 2 };

// Most critical recommendations first.
const sortedRecommendations = computed<IBriefingRecommendation[]>(() =>
    [...(state.briefing?.recommendations ?? [])].sort(
        (a, b) =>
            (PRIORITY_RANK[a.priority] ?? 3) - (PRIORITY_RANK[b.priority] ?? 3),
    ),
);

const severityClass = (severity: string): string => {
    if (severity === "critical") {
        return "border-red-200 bg-red-50/60 text-red-700";
    }
    if (severity === "warning") {
        return "border-amber-200 bg-amber-50/60 text-amber-700";
    }
    return "border-blue-200 bg-blue-50/60 text-blue-700";
};

const severityIcon = (severity: string): Component => {
    if (severity === "critical") {
        return ExclamationTriangleIcon;
    }
    if (severity === "warning") {
        return ExclamationCircleIcon;
    }
    return InformationCircleIcon;
};

const priorityClass = (priority: string): string => {
    if (priority === "high") {
        return "bg-red-100 text-red-700";
    }
    if (priority === "medium") {
        return "bg-amber-100 text-amber-700";
    }
    return "bg-gray-100 text-gray-600";
};

const priorityIcon = (priority: string): Component => {
    if (priority === "high") {
        return ExclamationTriangleIcon;
    }
    if (priority === "medium") {
        return ExclamationCircleIcon;
    }
    return InformationCircleIcon;
};

const priorityIconClass = (priority: string): string => {
    if (priority === "high") {
        return "text-red-500";
    }
    if (priority === "medium") {
        return "text-amber-500";
    }
    return "text-gray-400";
};

const formatDate = (value: string): string => new Date(value).toLocaleString();

const EVENT_LABEL_KEYS: Record<string, string> = {
    daily_briefing: "vue.ai_events_type_briefing",
    chat_created: "vue.ai_events_type_chat",
    content_created: "vue.ai_events_type_content",
    email_sent: "vue.ai_events_type_email",
};

const eventTypeLabel = (type: string): string => {
    const key = EVENT_LABEL_KEYS[type];
    return key ? t(key) : type;
};

const eventDotClass = (type: string): string => {
    if (type === "daily_briefing") {
        return "bg-[#FF421E]";
    }
    if (type === "chat_created") {
        return "bg-blue-400";
    }
    if (type === "content_created") {
        return "bg-green-400";
    }
    if (type === "email_sent") {
        return "bg-amber-400";
    }
    return "bg-gray-300";
};

// Poll immediately if the server says a generation is already in flight.
if (state.status === "generating") {
    startPolling();
}

onUnmounted(stopPolling);
</script>

<style scoped>
.generating-bubble {
    position: absolute;
    bottom: 100%;
    left: 50%;
    transform: translateX(-30%);
    margin-bottom: 0.25rem;
    white-space: nowrap;
    border-radius: 1rem;
    border-bottom-left-radius: 0.25rem;
    border: 1px solid rgb(255 255 255 / 0.6);
    background: rgb(255 255 255 / 0.95);
    padding: 0.35rem 0.75rem;
    font-size: 0.8rem;
    font-weight: 600;
    color: #ff421e;
    box-shadow: 0 8px 20px rgb(0 0 0 / 0.08);
    backdrop-filter: blur(6px);
    z-index: 10;
    animation: bubblePulse 1.4s ease-in-out infinite;
}

@keyframes bubblePulse {
    0%,
    100% {
        transform: translateX(-30%) translateY(0) scale(1);
        opacity: 1;
    }
    50% {
        transform: translateX(-30%) translateY(-3px) scale(1.05);
        opacity: 0.85;
    }
}

.briefing-robot {
    width: 84px;
    height: 84px;
    animation: briefingFloat 3s ease-in-out infinite;
}

.briefing-robot :deep(.robot-anim),
.briefing-robot :deep(.robot-anim svg) {
    width: 84px !important;
    height: 84px !important;
}

.briefing-robot :deep(#robotMessage) {
    display: none !important;
}

@keyframes briefingFloat {
    0%,
    100% {
        transform: translateY(0);
    }
    50% {
        transform: translateY(-8px);
    }
}

@media (prefers-reduced-motion: reduce) {
    .briefing-robot,
    .generating-bubble {
        animation: none !important;
    }
}
</style>
